import { useState, useEffect } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../lib/auth';
import { getActiveJobsCount } from '../../lib/api';
import UserMenu from './UserMenu';

export default function Navigation() {
  const { t } = useTranslation();
  const location = useLocation();
  const { workspaceSlug } = useParams<{ workspaceSlug: string }>();
  const { user, workspace, isAuthenticated } = useAuthStore();
  const [activeJobsCount, setActiveJobsCount] = useState(0);

  // Poll active jobs count
  useEffect(() => {
    if (!isAuthenticated) return;

    const fetchJobsCount = async () => {
      try {
        const { count } = await getActiveJobsCount();
        setActiveJobsCount(count);
      } catch (error) {
        console.error('Failed to fetch active jobs count:', error);
      }
    };

    // Initial fetch
    fetchJobsCount();

    // Poll every 5 seconds
    const interval = setInterval(fetchJobsCount, 5000);

    return () => clearInterval(interval);
  }, [isAuthenticated]);

  // Debug logging
  console.log('[NAV] Auth state:', { user, workspace, isAuthenticated });
  console.log('[NAV] Workspace slug from URL:', workspaceSlug);
  console.log('[NAV] localStorage auth-storage:', localStorage.getItem('auth-storage'));

  // Build nav items with workspace slug in URLs
  const navItems = workspaceSlug ? [
    { path: `/${workspaceSlug}/dashboard`, label: t('dashboard.nav.dashboard') },
    { path: `/${workspaceSlug}/create`, label: t('dashboard.nav.create') },
    { path: `/${workspaceSlug}/ideas`, label: t('dashboard.nav.ideas') },
    { path: `/${workspaceSlug}/approve`, label: t('dashboard.nav.approve') },
    { path: `/${workspaceSlug}/calendar`, label: t('dashboard.nav.calendar') },
    { path: `/${workspaceSlug}/statistics`, label: t('dashboard.nav.statistics') },
  ] : [];

  const isActive = (path: string) => {
    if (path.endsWith('/dashboard')) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  return (
    <nav className="bg-white dark:bg-gray-900 border-b-2 border-black dark:border-gray-700 transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo & Workspace Badge */}
          <div className="flex items-center gap-3">
            <Link
              to={workspaceSlug ? `/${workspaceSlug}/dashboard` : '/login'}
              className="flex items-center hover:opacity-80 transition-opacity"
            >
              <img
                src="/logo.png"
                alt={t('app.name')}
                className="h-16 dark:hidden"
              />
              <img
                src="/logo-white.png"
                alt={t('app.name')}
                className="h-16 hidden dark:block"
              />
            </Link>

            {workspace && (
              <div className="hidden md:flex flex-col">
                <span className="text-[0.5rem] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Workspace:
                </span>
                <span className="text-sm font-bold text-gray-900 dark:text-gray-100">
                  {workspace.name}
                </span>
              </div>
            )}
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`
                  relative text-lg font-medium transition-all duration-200
                  hover:text-black dark:hover:text-white
                  ${
                    isActive(item.path)
                      ? 'text-black dark:text-white font-bold border-b-2 border-black dark:border-white pb-1'
                      : 'text-gray-600 dark:text-gray-400'
                  }
                `}
              >
                {item.label}
                {/* Active jobs badge on Approve nav item */}
                {item.path.includes('/approve') && activeJobsCount > 0 && (
                  <span className="absolute -top-2 -right-3 bg-yellow-500 dark:bg-yellow-400 text-white dark:text-black rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold animate-pulse">
                    {activeJobsCount}
                  </span>
                )}
              </Link>
            ))}

            {/* Debug Auth Status - TEMPORARY */}
            {!user && (
              <div className="px-3 py-1 bg-yellow-100 dark:bg-yellow-900 border-2 border-yellow-500 rounded-lg text-xs">
                Not logged in
              </div>
            )}

            {/* User Menu */}
            <UserMenu />
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 rounded-lg border-2 border-black dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800"
            onClick={() => {
              // TODO: Implement mobile menu toggle
              console.log('Mobile menu clicked');
            }}
          >
            <svg
              className="w-6 h-6 text-black dark:text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile Navigation (TODO: Add slide-out menu) */}
      {/* For now, mobile users can use desktop navigation on small screens */}
    </nav>
  );
}
